import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { MEDICAL_SPECIALTIES, SPECIALTY_ALL_LABEL, specialtyLabel } from "@/i18n/translations";
import { CITY_NAMES as CITIES } from "@/data/cities";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableSelect from "@/components/SearchableSelect";
import Seo from "@/components/Seo";
import { Calendar, MapPin, Mail, ShieldCheck, Zap, Gift, X } from "lucide-react";

export default function Home() {
  const { t, refCode, clearRef, lang } = useApp();
  const nav = useNavigate();
  const [city, setCity] = useState("");
  const [specialty, setSpecialty] = useState("");
  const specialtyOptions = [
    { value: "__all__", label: SPECIALTY_ALL_LABEL[lang] || SPECIALTY_ALL_LABEL.es },
    ...MEDICAL_SPECIALTIES.map(s => ({ value: s, label: specialtyLabel(s, lang) })),
  ];
  const seoDesc = {
    es: "Reserve su cita médica en 2 clics con médicos verificados en Ecuador. Sin llamadas, con recordatorios automáticos.",
    fr: "Prenez rendez-vous avec un médecin vérifié en Équateur en 2 clics. Sans appels, avec rappels automatiques.",
    en: "Book a medical appointment in 2 clicks with verified doctors in Ecuador. No calls, automatic reminders.",
  }[lang] || undefined;

  const search = () => {
    const params = new URLSearchParams();
    if (city) params.set("city", city);
    if (specialty && specialty !== "__all__") params.set("specialty", specialty);
    nav(`/buscar?${params.toString()}`);
  };

  return (
    <div className="hero-bg">
      <Seo title="Citas médicas en línea en Ecuador" description={seoDesc}/>
      {refCode && (
        <div data-testid="ref-banner" className="bg-blue-600 text-white">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 py-3 flex flex-wrap items-center justify-center gap-3 text-sm">
            <Gift size={16}/>
            <span>Código de referido aplicado: <strong className="font-mono">{refCode}</strong></span>
            <Button data-testid="ref-banner-cta" size="sm" onClick={() => nav("/doctor/registro")}
                    className="bg-white text-blue-700 hover:bg-blue-50 font-semibold h-8">
              Registrarme como médico
            </Button>
            <button data-testid="ref-banner-close" onClick={clearRef} className="opacity-70 hover:opacity-100" aria-label="close"><X size={16}/></button>
          </div>
        </div>
      )}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span data-testid="hero-badge" className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
              <ShieldCheck size={14} /> Ecuador · 30 ciudades
            </span>
            <h1 data-testid="hero-title" className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              {t.home.title}
            </h1>
            <p data-testid="hero-subtitle" className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
              {t.home.subtitle}
            </p>

            <Card className="mt-8 border-slate-200 shadow-lg shadow-blue-900/5">
              <CardContent className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3">
                <Select value={city} onValueChange={setCity}>
                  <SelectTrigger data-testid="home-city-select" className="h-12">
                    <SelectValue placeholder={t.home.selectCity} />
                  </SelectTrigger>
                  <SelectContent>
                    {CITIES.map(c => <SelectItem key={c} data-testid={`home-city-${c}`} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <SearchableSelect
                  testid="home-specialty-select"
                  options={specialtyOptions}
                  value={specialty}
                  onChange={setSpecialty}
                  placeholder={t.home.selectSpecialty}
                  searchPlaceholder={t.common.searchPlaceholder || "Buscar…"}
                  emptyLabel={t.common.noResults || "Sin resultados"}
                  className="h-12"/>
                <Button data-testid="home-search-btn" onClick={search}
                        className="h-12 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-sm hover:shadow-md transition-all">
                  {t.home.searchCta}
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-xl shadow-blue-900/10 border border-slate-100">
              <img src="https://images.unsplash.com/photo-1758691462878-6edc3d3da1be?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                   alt="Consulta médica" className="w-full h-full object-cover"/>
              <div className="absolute inset-0 bg-gradient-to-t from-blue-950/40 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-2xl p-4 border border-white shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Zap className="text-emerald-600" size={20}/>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{t.home.trialBadge}</div>
                    <div className="text-xs text-slate-500">Para médicos · Agendik Pro</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 pb-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">{t.home.featuresTitle}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            {icon: Zap, title: t.home.f1t, desc: t.home.f1d, tone: "bg-blue-50 text-blue-600"},
            {icon: ShieldCheck, title: t.home.f2t, desc: t.home.f2d, tone: "bg-emerald-50 text-emerald-600"},
            {icon: Mail, title: t.home.f3t, desc: t.home.f3d, tone: "bg-amber-50 text-amber-600"},
          ].map((f, i) => (
            <Card key={i} data-testid={`feature-${i}`} className="border-slate-200 hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className={`w-11 h-11 rounded-xl ${f.tone} flex items-center justify-center mb-4`}>
                  <f.icon size={22}/>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{f.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="bg-gradient-to-br from-blue-600 to-blue-500">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div className="text-white">
            <h2 className="text-3xl sm:text-4xl font-bold">{t.home.doctorsTitle}</h2>
            <p className="mt-3 text-blue-50 text-base leading-relaxed max-w-md">{t.home.doctorsSub}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button data-testid="home-doctor-cta" onClick={() => nav("/doctor/registro")}
                      className="bg-white text-blue-700 hover:bg-blue-50 font-semibold h-12 px-6">
                {t.home.doctorCta}
              </Button>
              <Button data-testid="home-partner-cta" onClick={() => nav("/socio/registro")}
                      variant="outline" className="bg-transparent border-white/60 text-white hover:bg-white/10 font-semibold h-12 px-6">
                {t.footer.becomePartner}
              </Button>
            </div>
          </div>
          <div className="bg-white/95 backdrop-blur rounded-2xl p-6 border border-white/40">
            <div className="text-xs font-semibold uppercase tracking-wider text-blue-600">Agendik Pro</div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-4xl font-extrabold text-slate-900">$49</span>
              <span className="text-slate-500">{t.home.priceMonth}</span>
            </div>
            <div className="text-sm text-emerald-700 font-semibold mt-1">{t.home.trialFull}</div>
            <ul className="mt-5 space-y-2 text-sm text-slate-700">
              <li className="flex items-center gap-2"><Calendar size={16} className="text-blue-600"/> {t.home.benefit1}</li>
              <li className="flex items-center gap-2"><MapPin size={16} className="text-blue-600"/> {t.home.benefit2}</li>
              <li className="flex items-center gap-2"><Mail size={16} className="text-blue-600"/> {t.home.benefit3}</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
